import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import { Box, Paper, Grid, Button, Tabs, Tab, Typography, Pagination } from '@mui/material';
import { useSnackbar } from 'notistack';
import { MdAdd } from 'react-icons/md';
import { LockOutlined } from '@mui/icons-material';
import { WORKFLOW_PAGE } from '../utils/commonConstants';
import SearchAndFilter from '../reusable-component/SearchAndFilter';
import AccordionTable from '../reusable-component/AccordionTable';
import ClientNoData from '../components/Client/ClientNoData';
import AddWorkflowModal from '../reusable-component/AddWorkflowModal';
import LoadingIcon from '../reusable-component/LoadingIcon';
import {
  getAllWorkflowApi,
  getAllWorkflowByProcessType,
  getWorkflowByUserGroup,
  addToFavourite,
  removeFromFavourite,
  getAllFavourite,
} from "../api's/WorkflowApi";
import { AuthContext } from '../context/AuthProvider';
import AccessModal from '../reusable-component/AccessModal';
import { WorkflowContext } from '../contexts/WorkflowProvider';
import { DateConverter } from '../utils/DateConverter';
import { getDomainByUserGroup } from "../api's/DomainApi";
import { getClientByUserGroupAndDomainIdApi } from "../api's/ClientApi";
import { getEngagementByUserGroupAndClientIdApi } from "../api's/EngagementApi";

export default function ClusterPage({ processType, processWorkflowType }) {
  const [value, setValue] = useState(() => {
    const storedTab = sessionStorage.getItem('cluster_tab_value');
    return storedTab !== null ? parseInt(storedTab, 10) : 0;
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(() => {
    const page = sessionStorage.getItem('cluster_page');
    return page !== null ? parseInt(page, 10) : 1;
  });
  const [favoritesPage, setFavoritesPage] = useState(1);
  const rowsPerPage = 5;
  const [domain, setDomain] = useState(() => {
    const storedValue = sessionStorage.getItem('filtered_cluster_domain');
    return storedValue !== null ? storedValue : '';
  });
  const [client, setClient] = useState(() => {
    const storedValue = sessionStorage.getItem('filtered_cluster_client');
    return storedValue !== null ? storedValue : '';
  });
  const [engagement, setEngagement] = useState(() => {
    const storedValue = sessionStorage.getItem('filtered_cluster_engagement');
    return storedValue !== null ? storedValue : '';
  });
  const [search, setSearch] = useState(() => {
    const storedValue = sessionStorage.getItem('filtered_cluster_search');
    return storedValue !== null ? storedValue : '';
  });
  const [domainList, setDomainList] = useState(() => {
    const storeList = sessionStorage.getItem('cluster_domainList');
    return storeList ? JSON.parse(storeList) : [];
  });
  const [clientList, setClientList] = useState(() => {
    const storeList = sessionStorage.getItem('cluster_clientList');
    return storeList ? JSON.parse(storeList) : [];
  });
  const [engagementList, setEngagementList] = useState(() => {
    const storeList = sessionStorage.getItem('cluster_engagementList');
    return storeList ? JSON.parse(storeList) : [];
  });
  const [selectedDomainId, setSelectedDomainId] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [tableData, setTableData] = useState([]);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const [openModal, setOpenModal] = useState(false);
  const { userGroup } = useContext(AuthContext);
  const [openAccessModal, setOpenAccessModal] = useState(false);
  const [favoriteWorkflows, setFavoriteWorkflows] = useState(() => {
    const storedFavorites = sessionStorage.getItem('favorite_cluster_workflows');
    return storedFavorites ? JSON.parse(storedFavorites) : [];
  });
  const [favoriteWorkflowData, setFavoriteWorkflowData] = useState([]);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalFavoriteCount, setTotalFavoriteCount] = useState(0);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterClientLoading, setFilterClientLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const debounceTimer = useRef(null);
  const skipNextEffectRef = useRef(false);
  const [prevProcessType, setPrevProcessType] = useState();
  const userPermissions = JSON.parse(sessionStorage.getItem('userPermissions') || '[]');
  const hasCreatePermission = userPermissions.includes('CREATE_CLIENT_ENGAGEMENT_WORKFLOW');
  const hasViewFavoritesPermission = userPermissions.includes('VIEW_FAVORITES');

  const type =
    processType === 'Asset 360'
      ? 'Asset 360'
      : processType === 'Customer 360'
      ? 'Customer 360'
      : processType === 'Company 360'
      ? 'Company 360'
      : 'data';

  const debouncedFetchTableData = (pageNumber, searchText) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchTableData(pageNumber, searchText);
    }, 500);
  };

  const debouncedFetchFavouriteData = (pageNumber, searchText) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      fetchFavoriteWorkflows(pageNumber, searchText);
    }, 500);
  };

  useEffect(() => {
    const initializeFilters = async () => {
      await fetchDomains();

      const filtered_domain = sessionStorage.getItem('filtered_cluster_domain') || '';
      const filtered_client = sessionStorage.getItem('filtered_cluster_client') || '';
      const filtered_engagement = sessionStorage.getItem('filtered_cluster_engagement') || '';

      if (filtered_domain) {
        const domainObj = domainList.find((d) => d.domain_name === filtered_domain);
        if (domainObj) {
          setDomain(filtered_domain);
          setSelectedDomainId(domainObj.domain_id);

          const clients = await getClientByUserGroupAndDomainIdApi(domainObj.domain_id);
          if (clients.status === 200) {
            setClientList(clients.data.data);
            sessionStorage.setItem('cluster_clientList', JSON.stringify(clients.data.data));

            if (filtered_client) {
              const clientObj = clients.data.data.find((c) => c.client_name === filtered_client);
              if (clientObj) {
                setClient(filtered_client);
                setSelectedClientId(clientObj.client_id);

                const engagements = await getEngagementByUserGroupAndClientIdApi(userGroup, clientObj.client_id);
                if (engagements.status === 200) {
                  setEngagementList(engagements.data);
                  sessionStorage.setItem('cluster_engagementList', JSON.stringify(engagements.data));

                  if (filtered_engagement) {
                    const engagementExists = engagements.data.some((e) => e.engagement_name === filtered_engagement);
                    if (engagementExists) {
                      setEngagement(filtered_engagement);
                    }
                  }
                }
              }
            }
          }
        }
      }

      setIsInitialized(true);
    };

    initializeFilters();

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isInitialized) return;

    if (skipNextEffectRef.current) {
      skipNextEffectRef.current = false;
      return;
    }

    if (value === 1) {
      fetchFavoriteWorkflows(favoritesPage, search);
    } else {
      fetchTableData(page, search);
    }
  }, [processType, processWorkflowType, search, value, page, favoritesPage, domain, client, engagement, isInitialized]);

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const response = await getDomainByUserGroup(userGroup);
      if (response.status === 200) {
        const domains = response?.data || [];
        setDomainList(domains);
        sessionStorage.setItem('cluster_domainList', JSON.stringify(domains));
      }
    } catch (error) {
      enqueueSnackbar('Failed to load domains', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
    setLoading(false);
  };

  const fetchClients = async (domainId) => {
    setFilterClientLoading(true);
    try {
      const clients = await getClientByUserGroupAndDomainIdApi(domainId);
      if (clients.status === 200) {
        setClientList(clients.data.data);
        sessionStorage.setItem('cluster_clientList', JSON.stringify(clients.data.data));
      }
    } catch (error) {
      enqueueSnackbar('Failed to load clients', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setFilterClientLoading(false);
    }
  };

  const fetchEngagements = async (clientId) => {
    setFilterLoading(true);
    try {
      const engagements = await getEngagementByUserGroupAndClientIdApi(userGroup, clientId);
      if (engagements.status === 200) {
        setEngagementList(engagements.data);
        sessionStorage.setItem('cluster_engagementList', JSON.stringify(engagements.data));
      }
    } catch (error) {
      enqueueSnackbar('Failed to load engagements', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setFilterLoading(false);
    }
  };

  const handleModal = () => {
    setOpenModal(!openModal);
  };

  const handleSearch = () => {
    setPage(1);
    sessionStorage.setItem('cluster_page', 1);
    setFavoritesPage(1);
    if (value === 1) {
      fetchFavoriteWorkflows(1, search);
    } else {
      fetchTableData(1, search);
    }
  };

  const handleDomain = async (event) => {
    const value = event.target.value;
    sessionStorage.setItem('filtered_cluster_domain', value);
    sessionStorage.removeItem('filtered_cluster_client');
    sessionStorage.removeItem('filtered_cluster_engagement');

    const selectedDomainObj = domainList.find((d) => d.domain_name === value);
    const domainId = selectedDomainObj ? selectedDomainObj.domain_id : null;
    setSelectedDomainId(domainId);

    setClient('');
    setEngagement('');
    setEngagementList([]);
    setDomain(value);
    setPage(1);
    sessionStorage.setItem('cluster_page', 1);
    setFavoritesPage(1);

    if (domainId) {
      await fetchClients(domainId);
    }
  };

  const handleClient = async (event) => {
    const value = event.target.value;
    sessionStorage.setItem('filtered_cluster_client', value);
    sessionStorage.removeItem('filtered_cluster_engagement');

    const selectedClientObj = clientList.find((client) => client.client_name === value);
    const clientId = selectedClientObj ? selectedClientObj.client_id : null;
    setSelectedClientId(clientId);

    setClient(value);
    setEngagement('');
    setPage(1);
    sessionStorage.setItem('cluster_page', 1);
    setFavoritesPage(1);

    if (clientId) {
      await fetchEngagements(clientId);
    }
  };

  const handleEngagement = (event) => {
    const value = event.target.value;
    sessionStorage.setItem('filtered_cluster_engagement', value);
    setEngagement(value);
    setPage(1);
    sessionStorage.setItem('cluster_page', 1);
    setFavoritesPage(1);
  };

  const handleOnChange = (event) => {
    const newValue = event.target.value;
    sessionStorage.setItem('filtered_cluster_search', newValue);
    setSearch(newValue);
    setPage(1);
    sessionStorage.setItem('cluster_page', 1);
    setFavoritesPage(1);

    skipNextEffectRef.current = true;

    if (value === 1) {
      debouncedFetchFavouriteData(1, newValue);
    } else {
      debouncedFetchTableData(1, newValue);
    }
  };

  const handleChangePage = (event, newPage) => {
    if (value === 1) {
      setFavoritesPage(newPage);
    } else {
      setPage(newPage);
      sessionStorage.setItem('cluster_page', newPage);
    }
  };

  const handleClearFilter = () => {
    sessionStorage.removeItem('filtered_cluster_domain');
    sessionStorage.removeItem('filtered_cluster_client');
    sessionStorage.removeItem('filtered_cluster_engagement');
    sessionStorage.removeItem('filtered_cluster_search');
    sessionStorage.removeItem('cluster_page');

    sessionStorage.removeItem('cluster_clientList');
    sessionStorage.removeItem('cluster_engagementList');

    setDomain('');
    setClient('');
    setEngagement('');
    setSearch('');
    setPage(1);
    setFavoritesPage(1);

    setSelectedDomainId('');
    setSelectedClientId('');

    setClientList([]);
    setEngagementList([]);
  };

  const handleAccessModal = () => {
    setOpenAccessModal(!openAccessModal);
  };

  const toggleFavorite = async (workflowId) => {
    setFavoriteLoading(true);
    try {
      const isFavorite = favoriteWorkflows.includes(workflowId);
      const apiCall = isFavorite ? removeFromFavourite : addToFavourite;

      const response = await apiCall(workflowId);
      if (response.status === 200) {
        enqueueSnackbar(isFavorite ? 'Removed from favorites' : 'Added to favorites', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });

        if (value === 1) {
          if (favoriteWorkflowData.length === 1 && favoritesPage > 1) {
            setFavoritesPage(favoritesPage - 1);
          } else {
            fetchFavoriteWorkflows(favoritesPage, search);
          }
        } else {
          fetchTableData(page, search);
        }
        setFavoriteWorkflows((prev) => (isFavorite ? prev.filter((id) => id !== workflowId) : [...prev, workflowId]));
      }
    } catch (error) {
      enqueueSnackbar('Failed to update favorites', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setFavoriteLoading(false);
    }
  };

  const fetchFavoriteWorkflows = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const currentPage = page - 1;
      const pageSize = rowsPerPage;
      if (prevProcessType !== type) {
        setPage(1);
        sessionStorage.setItem('cluster_page', 1);
        setPrevProcessType(type);
      }
      const currentProcessType = type === 'data' ? null : type;

      const payload = {
        page: currentPage,
        size: pageSize,
        process_type: currentProcessType,
        workflow_name: search || null,
        engagement_name: engagement || null,
        client_name: client || null,
        domain_name: domain || null,
      };

      const response = await getAllFavourite(payload);

      if (response.status === 200) {
        const favorites = response.data?.workflowData || [];
        const favIds = favorites.map((w) => w.workflow_id);

        setTotalFavoriteCount(response.data.totalData || 0);
        setFavoriteWorkflows(favIds);
        setFavoriteWorkflowData(favorites);
        sessionStorage.setItem('favorite_cluster_workflows', JSON.stringify(favIds));
      }
    } catch (error) {
      enqueueSnackbar('Failed to load favorites', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchTableData = async (page = 1, search = '') => {
    setLoading(true);
    try {
      const currentPage = page - 1;
      const pageSize = rowsPerPage;
      if (prevProcessType !== type) {
        setPage(1);
        sessionStorage.setItem('cluster_page', 1);
        setPrevProcessType(type);
      }
      const currentProcessType = type === 'data' ? null : type;

      const payload = {
        page: currentPage,
        size: pageSize,
        process_type: currentProcessType,
        workflow_name: search || null,
        engagement_name: engagement || null,
        client_name: client || null,
        domain_name: domain || null,
      };

      const response = await getAllWorkflowApi(payload);

      if (response.status === 200) {
        setTotalCount(response.data.totalData || 0);
        setTableData(response.data.workflowData || []);
        setFilteredTableData(response.data.workflowData || []);
      }
    } catch (error) {
      console.log(error);
      setTableData([]);
      setFilteredTableData([]);
      if (error.response?.status === 500) {
        enqueueSnackbar('Internal server error', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    }
    setLoading(false);
  };

  const filteredData = useMemo(() => {
    if (value === 1) {
      return favoriteWorkflowData;
    }
    return filteredTableData;
  }, [value, favoriteWorkflowData, filteredTableData]);

  const paginationCount = useMemo(() => {
    const totalItems = value === 1 ? totalFavoriteCount : totalCount;
    return Math.ceil(totalItems / rowsPerPage);
  }, [value, totalFavoriteCount, totalCount, rowsPerPage]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    sessionStorage.setItem('cluster_tab_value', newValue);
    setPage(1);
    sessionStorage.setItem('cluster_page', 1);
    setFavoritesPage(1);
  };

  function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        style={{ width: '100%' }}
        {...other}
      >
        {value === index && (
          <Box>
            <Typography>{children}</Typography>
          </Box>
        )}
      </div>
    );
  }

  function a11yProps(index) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const data = {
    search,
    handleSearch,
    handleOnChange,
    region: domain,
    handleRegion: handleDomain,
    handleClearFilter,
    handleStatus: handleClient,
    status: client,
    handleEngagement,
    engagement,
    firstFilterText: 'Filter By Domain',
    secondFilterText: 'Filter By Client',
    thirdFilterText: 'Filter By Engagement',
    clearFilterText: WORKFLOW_PAGE.workflowClearFilterButton,
    firstFilterValues: {
      filterData: domainList.map((item) => item.domain_name),
    },
    secondFilterValues: {
      filterData: clientList.map((item) => item.client_name),
    },
    thirdFilterValues: {
      filterData: engagementList.map((item) => item.engagement_name),
    },
    isFirstFilterVisible: true,
    isSecondFilterVisible: !!domain, // Show client filter only when domain is selected
    isThirdFilterVisible: !!client, // Show engagement filter only when client is selected
    filterLoading,
    filterClientLoading,
  };

  return (
    <>
      <Grid container direction="row" display="flex">
        <Grid item xs={6} direction="row" justifyContent="flex-start" style={{ color: '#43425D' }}>
          All Workflow
        </Grid>
        <Grid item xs={6} display="flex" justifyContent="flex-end">
          <Button
            type="submit"
            variant="contained"
            sx={{ backgroundColor: '#00043C', '&:hover': { backgroundColor: '#00043C' } }}
            onClick={handleModal}
            disabled={!hasCreatePermission}
          >
            <MdAdd />
            Add New Workflow
          </Button>
        </Grid>
      </Grid>
      <AddWorkflowModal handleModal={handleModal} open={openModal} fetchTableData={fetchTableData} processType={type} />
      <AccessModal openAccessModal={openAccessModal} handleAccessModal={handleAccessModal} />
      <Box sx={{ borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          TabIndicatorProps={{
            style: {
              backgroundColor: '#47D7AC',
            },
          }}
          sx={{ '& .MuiTab-root.Mui-selected': { color: '#00043C' } }}
        >
          <Tab label="All" {...a11yProps(0)} sx={{ fontWeight: 'bold' }} />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                Favourite
                {!hasViewFavoritesPermission && <LockOutlined sx={{ fontSize: 16 }} />}
              </Box>
            }
            {...a11yProps(1)}
            sx={{ fontWeight: 'bold' }}
            disabled={!hasViewFavoritesPermission}
          />
        </Tabs>
      </Box>
      <SearchAndFilter data={data} key="cluster-page" />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          alignContent: 'center',
        }}
      >
        <TabPanel value={value} index={value}>
          {loading ? (
            <LoadingIcon />
          ) : filteredData.length === 0 ? (
            !loading && (
              <Paper elevation={3} sx={{ height: '330px', marginTop: '10px', width: '100%' }}>
                <ClientNoData
                  title={WORKFLOW_PAGE.workflowPageNoDataToDisplay}
                  text={WORKFLOW_PAGE.noWorkflowToReview}
                  addBtnText={value === 1 ? 'Browse Workflows' : undefined}
                  handleOnClick={value === 1 ? () => setValue(0) : undefined}
                  isAddBtn={value === 1}
                />
              </Paper>
            )
          ) : (
            <AccordionTable
              data={filteredData}
              type={value === 1 ? 'favorite' : 'all'}
              fetchTableData={fetchTableData}
              rowsPerPage={rowsPerPage}
              page={value === 1 ? favoritesPage : page}
              processType={processType}
              toggleFavorite={toggleFavorite}
              favoriteWorkflows={favoriteWorkflows}
            />
          )}
        </TabPanel>
      </Box>
      {filteredData.length > 0 && !loading && (
        <Pagination
          count={paginationCount}
          page={value === 1 ? favoritesPage : page}
          onChange={handleChangePage}
          variant="outlined"
          shape="rounded"
          size="small"
          sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
        />
      )}
    </>
  );
}
